import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ShieldCheckIcon, 
  GlobeAltIcon, 
  UserGroupIcon,
  TrophyIcon,
  BuildingLibraryIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  LightBulbIcon,
  HeartIcon,
  StarIcon,
  BanknotesIcon,
  ScaleIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const About = () => {
  const stats = [
    { label: 'Years of Excellence', value: '15+', icon: TrophyIcon },
    { label: 'Global Offices', value: '25', icon: BuildingLibraryIcon },
    { label: 'Professional Staff', value: '2,500+', icon: UserGroupIcon },
    { label: 'Assets Under Management', value: '$500B+', icon: BanknotesIcon },
    { label: 'Daily Trading Volume', value: '$12.5B+', icon: ChartBarIcon },
    { label: 'Client Satisfaction', value: '98.7%', icon: StarIcon }
  ];

  const values = [
    {
      icon: ShieldCheckIcon,
      title: 'Integrity First',
      description: 'We operate with unwavering ethical standards, ensuring transparency and trust in every client interaction.',
      color: 'blue'
    },
    {
      icon: LightBulbIcon,
      title: 'Innovation Drive',
      description: 'Continuously pushing the boundaries of financial technology to deliver cutting-edge capital solutions.',
      color: 'amber'
    },
    {
      icon: UserGroupIcon,
      title: 'Client Excellence',
      description: 'Our clients\' success is our success. We provide personalized service to help achieve financial goals.',
      color: 'emerald'
    },
    {
      icon: GlobeAltIcon,
      title: 'Global Reach',
      description: 'Connecting investors worldwide with comprehensive market access and localized support in every region.',
      color: 'purple'
    },
    {
      icon: ScaleIcon,
      title: 'Regulatory Excellence',
      description: 'Maintaining the highest regulatory standards and compliance across all jurisdictions we operate in.',
      color: 'red'
    },
    {
      icon: HeartIcon,
      title: 'Social Responsibility',
      description: 'Committed to sustainable business practices and giving back to the communities we serve.',
      color: 'rose'
    }
  ];

  const colorMap = {
    blue: 'bg-blue-100 text-blue-600 border-blue-200',
    amber: 'bg-amber-100 text-amber-600 border-amber-200',
    emerald: 'bg-emerald-100 text-emerald-600 border-emerald-200',
    purple: 'bg-purple-100 text-purple-600 border-purple-200',
    red: 'bg-red-100 text-red-600 border-red-200',
    rose: 'bg-rose-100 text-rose-600 border-rose-200',
  };

  const leadership = [
    {
      name: 'Richard Caldwell',
      role: 'Chief Executive Officer',
      bio: 'A distinguished capital markets leader with over 22 years driving growth across global investment firms. Richard co-founded INTRA CAPITAL with a vision to make institutional-grade trading accessible to all.',
      image: '/images/people/pexels-yankrukov-7793146.jpg',
      experience: '22+ Years',
      specialization: 'Strategic Leadership'
    },
    {
      name: 'Natalie Voss',
      role: 'Chief Financial Officer',
      bio: 'Brings deep expertise in corporate treasury, international capital allocation, and financial governance. Natalie oversees all financial operations and investor relations across INTRA CAPITAL.',
      image: '/images/people/pexels-yankrukov-7693223.jpg',
      experience: '15+ Years',
      specialization: 'Corporate Finance'
    },
    {
      name: 'Daniel Hartmann',
      role: 'Chief Technology Officer',
      bio: 'A fintech architect who has designed and scaled trading infrastructure for some of Europe\'s fastest-growing investment platforms. Daniel leads all technology, product, and engineering at INTRA CAPITAL.',
      image: '/images/people/pexels-tima-miroshnichenko-7567598.jpg',
      experience: '14+ Years',
      specialization: 'Financial Technology'
    },
    {
      name: 'Marcus Ferreira',
      role: 'Chief Risk Officer',
      bio: 'Expert in global regulatory compliance and multi-asset portfolio risk. Marcus previously served as Head of Risk at a leading London-based hedge fund before joining INTRA CAPITAL at inception.',
      image: '/images/people/pexels-pavel-danilyuk-5520284.jpg',
      experience: '17+ Years',
      specialization: 'Risk Management'
    }
  ];

  const timeline = [
    {
      year: '2021',
      title: 'Foundation',
      description: 'INTRA CAPITAL LIMITED incorporated in England and Wales with a clear mission — to bring institutional-grade investment tools to every investor.',
      icon: BuildingLibraryIcon
    },
    {
      year: '2022',
      title: 'Platform Launch',
      description: 'Launched our core trading platform, onboarding the first wave of clients across the UK and Europe.',
      icon: GlobeAltIcon
    },
    {
      year: '2022',
      title: 'Digital Assets',
      description: 'Introduced comprehensive cryptocurrency and digital asset trading, giving clients access to 200+ tokens.',
      icon: CurrencyDollarIcon
    },
    {
      year: '2023',
      title: 'Technology Expansion',
      description: 'Rolled out AI-powered analytics, copy trading, and automated bot strategies to our growing client base.',
      icon: LightBulbIcon
    },
    {
      year: '2024',
      title: 'Global Growth',
      description: 'Expanded operations to 150+ countries, welcoming 500,000+ active clients and crossing $50B in volume traded.',
      icon: UserGroupIcon
    },
    {
      year: '2025',
      title: 'Market Leadership',
      description: 'Recognised as a leading capital markets platform by multiple global industry organisations.',
      icon: TrophyIcon
    }
  ];

  const achievements = [
    { title: 'Best Investment Platform 2024', organization: 'FinTech Excellence Awards', year: '2024' },
    { title: 'Most Innovative Capital Firm', organization: 'Global Finance Awards', year: '2023' },
    { title: 'Top Security Implementation', organization: 'CyberSecurity Institute', year: '2023' },
    { title: 'Excellence in Client Service', organization: 'Industry Choice Awards', year: '2022' },
    { title: 'Technology Innovation Leader', organization: 'TradeTech Summit', year: '2022' },
    { title: 'Best Mobile Trading App', organization: 'Mobile Finance Awards', year: '2021' }
  ];

  return (
    <div className="min-h-screen bg-white w-full overflow-x-hidden">

      {/* Hero */}
      <section className="relative py-24 sm:py-32 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 overflow-hidden mt-16 lg:mt-20">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500/8 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-amber-400/5 rounded-full blur-3xl"></div>
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center px-4 py-2 bg-amber-500/10 border border-amber-400/20 rounded-full mb-6">
                <span className="text-amber-300 text-sm font-medium">About INTRA CAPITAL LIMITED</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Pioneering the Future of
                <span className="block bg-gradient-to-r from-amber-400 to-yellow-400 bg-clip-text text-transparent mt-1">
                  Capital Investment
                </span>
              </h1>
              <p className="text-lg text-slate-400 leading-relaxed max-w-lg">
                For over 15 years, INTRA CAPITAL LIMITED has been at the forefront of financial 
                innovation, delivering institutional-grade investment solutions to professionals worldwide.
              </p>
              <div className="flex gap-4 mt-8">
                <Link
                  to="/contact"
                  className="inline-flex items-center px-6 py-3 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold rounded-xl transition-all duration-300"
                >
                  Get in Touch
                  <ArrowRightIcon className="ml-2 h-4 w-4" />
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center px-6 py-3 bg-white/5 border border-white/10 text-white font-semibold rounded-xl hover:bg-white/10 transition-all duration-300"
                >
                  Our Services
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden border border-amber-500/20 shadow-2xl">
                <img
                  src="/images/new-images/investment-strategy.jpg"
                  alt="INTRA CAPITAL professional team"
                  className="w-full h-auto object-cover"
                  onError={(e) => { e.target.src = '/images/new-images/team-collaboration.jpg'; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 to-transparent"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-slate-50 border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="text-center group"
              >
                <div className="flex justify-center mb-3">
                  <div className="p-3 rounded-xl bg-white border border-slate-200 group-hover:border-amber-300 group-hover:shadow-md transition-all duration-300">
                    <stat.icon className="h-6 w-6 text-amber-600" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-slate-900 mb-1">{stat.value}</div>
                <div className="text-sm text-slate-500">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-8 leading-tight">
                Our Story of Excellence
              </h2>
              <div className="space-y-5 text-slate-600 leading-relaxed">
                <p>
                  Founded in 2021 by a team of seasoned finance professionals and technology innovators, 
                  INTRA CAPITAL LIMITED emerged from a simple yet powerful vision: to democratize 
                  access to professional-grade investment tools and capital market intelligence.
                </p>
                <p>
                  What started as a boutique firm serving institutional clients has evolved into 
                  a global financial technology leader, serving over 250,000 active investors across 
                  150+ countries. Our commitment to innovation, security, and client success has 
                  earned us recognition as the industry's most trusted capital platform.
                </p>
                <p>
                  Today, we manage over $500 billion in assets and process more than $12.5 billion 
                  in daily trading volume, while maintaining our core values of integrity, 
                  innovation, and client-first service.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-xl">
                <img 
                  src="/images/new-images/team-collaboration.jpg" 
                  alt="INTRA CAPITAL Team" 
                  className="w-full h-auto object-cover"
                  onError={(e) => { e.target.src = '/images/new-images/office-professional.jpg'; }}
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-amber-500/10 to-transparent rounded-3xl"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-5">Our Core Values</h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              The principles that guide every decision we make and every service we deliver.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="bg-white rounded-2xl p-7 border border-slate-200 hover:border-amber-300 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 h-full">
                  <div className={`inline-flex p-3 rounded-xl border mb-5 ${colorMap[value.color]}`}>
                    <value.icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{value.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{value.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-5">World-Class Leadership</h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Our executive team brings decades of experience from top-tier financial institutions and technology companies.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {leadership.map((leader, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
              >
                <div className="bg-white rounded-2xl p-6 border border-slate-200 text-center hover:border-amber-300 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                  <div className="relative mb-5">
                    <div className="w-full aspect-[3/4] rounded-xl overflow-hidden mb-2">
                      <img 
                        src={leader.image} 
                        alt={leader.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-1">{leader.name}</h3>
                  <div className="text-amber-600 font-semibold text-sm mb-3">{leader.role}</div>
                  <p className="text-slate-500 text-sm mb-5 leading-relaxed">{leader.bio}</p>
                  <div className="flex justify-between text-xs border-t border-slate-100 pt-4">
                    <div className="text-center">
                      <div className="font-bold text-slate-900">{leader.experience}</div>
                      <div className="text-slate-400">Experience</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-slate-900 text-xs">{leader.specialization}</div>
                      <div className="text-slate-400">Expertise</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-slate-950 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-5">Our Journey of Innovation</h2>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto">
              Key milestones that shaped our evolution into a global capital markets leader.
            </p>
          </motion.div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-px h-full bg-amber-500/30 hidden lg:block"></div>
            <div className="space-y-12">
              {timeline.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -40 : 40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: index * 0.1 }}
                  className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                >
                  <div className={`w-full lg:w-1/2 ${index % 2 === 0 ? 'lg:pr-12' : 'lg:pl-12'}`}>
                    <div className="bg-slate-900 border border-slate-800 hover:border-amber-500/30 rounded-2xl p-6 transition-all duration-300">
                      <div className="flex items-center mb-4">
                        <div className="p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20 mr-3">
                          <item.icon className="h-5 w-5 text-amber-400" />
                        </div>
                        <div className="text-xl font-bold text-amber-400">{item.year}</div>
                      </div>
                      <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                      <p className="text-slate-400 leading-relaxed text-sm">{item.description}</p>
                    </div>
                  </div>
                  <div className="relative z-10 hidden lg:flex">
                    <div className="w-4 h-4 bg-amber-500 rounded-full border-4 border-slate-950"></div>
                  </div>
                  <div className="hidden lg:block lg:w-1/2"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Awards */}
      <section className="py-24 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-5">Industry Recognition</h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Our commitment to excellence recognized by leading global industry organizations.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {achievements.map((achievement, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
              >
                <div className="bg-white rounded-2xl p-6 border border-slate-200 hover:border-amber-300 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 text-center">
                  <TrophyIcon className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                  <h3 className="text-base font-bold text-slate-900 mb-2">{achievement.title}</h3>
                  <p className="text-amber-600 font-semibold text-sm mb-1">{achievement.organization}</p>
                  <p className="text-slate-400 text-sm">{achievement.year}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Presence */}
      <section className="py-24 bg-slate-950 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">
                Global Presence,<br />Local Expertise
              </h2>
              <p className="text-lg text-slate-400 mb-8 leading-relaxed">
                With offices across North America, Europe, Asia, and Australia, we provide 
                24/7 support and localized services to our global client base.
              </p>
              <div className="grid grid-cols-2 gap-6">
                {[
                  { value: '25', label: 'Global Offices' },
                  { value: '150+', label: 'Countries Served' },
                  { value: '50+', label: 'Languages Supported' },
                  { value: '24/7', label: 'Global Support' }
                ].map((stat, i) => (
                  <div key={i} className="text-center p-5 bg-slate-900 border border-slate-800 rounded-xl hover:border-amber-500/30 transition-all duration-300">
                    <div className="text-3xl font-bold text-amber-400 mb-1">{stat.value}</div>
                    <div className="text-sm text-slate-400">{stat.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden border border-amber-500/20 shadow-xl">
                <img 
                  src="/images/new-images/global-markets.jpg" 
                  alt="INTRA CAPITAL Global Operations" 
                  className="w-full h-auto object-cover"
                  onError={(e) => { e.target.src = '/images/new-images/financial-growth.jpg'; }}
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-amber-500/10 to-transparent"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-amber-500 to-yellow-500">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-950 mb-6">
              Join the INTRA CAPITAL Family
            </h2>
            <p className="text-lg text-slate-800 mb-10 max-w-3xl mx-auto leading-relaxed">
              Experience the difference of working with a capital firm that puts your success first. 
              Discover why over 250,000 investors trust us with their financial future.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="http://dashboard.intracapitals.com/" className="bg-slate-950 text-white hover:bg-slate-800 px-10 py-4 rounded-xl font-bold text-base transition-colors duration-300">
                Open Account Today
              </a>
              <Link to="/contact" className="bg-white/20 backdrop-blur-sm border-2 border-slate-950/20 text-slate-950 hover:bg-white/40 px-10 py-4 rounded-xl font-bold text-base transition-all duration-300">
                Contact Our Team
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
